import {
  changedReportLines,
  type DictatedReportSave,
  splitDictations,
} from "@rosc/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  type DictationTemplate,
  saveDictatedReport,
} from "@/lib/dictation-templates";
import type {
  DictationReportPhase,
  DictationReportState,
} from "./useDictationReport";
import { EMPTY_DICTATION_REPORT } from "./useDictationReport";

// Every dictated report is kept on the server as a visit, saved when it is
// filled and again after each Dictate more, Undo or template switch. Nothing
// is kept in the browser (the report is patient data).

export type ReportSaveStatus = "idle" | "saving" | "saved" | "error";

/**
 * A saved report's title: the template name (the visit list already shows
 * the time). A title the user changed (renamed visit) is kept instead.
 */
export const dictatedReportTitle = (
  templateName: string,
  customTitle: string | null = null,
): string => customTitle ?? templateName;

/** What is saved for this state, or null while there is nothing to keep. */
export const dictatedReportBody = (
  state: DictationReportState,
  title: string,
): DictatedReportSave | null => {
  if (!state.report?.trim() || state.dictations.length === 0) return null;
  return {
    title,
    report: state.report,
    dictations: state.dictations,
    templatePublicId: state.template?.id ?? null,
  };
};

/** A saved report (visit) as the dictation screen state. */
export const reportStateFromVisit = (
  visit: {
    result: string | null;
    transcription: string | null;
    customTemplatePublicId: string | null;
  },
  templates: readonly DictationTemplate[],
): DictationReportState => {
  const template =
    templates.find((item) => item.id === visit.customTemplatePublicId) ?? null;
  const report = visit.result ?? "";
  return {
    ...EMPTY_DICTATION_REPORT,
    template,
    dictations: splitDictations(visit.transcription),
    report,
    changedLines: template ? changedReportLines(template.body, report) : [],
  };
};

export function useDictatedReportSaving({
  state,
  phase,
  savedId = null,
  savedTitle = null,
}: {
  state: DictationReportState;
  phase: DictationReportPhase;
  // The visit id of a report opened again.
  savedId?: string | null;
  // Its title: kept on later saves when the user renamed it.
  savedTitle?: string | null;
}) {
  const [id, setId] = useState<string | null>(savedId);
  const [status, setStatus] = useState<ReportSaveStatus>(
    savedId ? "saved" : "idle",
  );
  const idRef = useRef(savedId);
  // A renamed report keeps its name; otherwise the title follows the
  // template (also after a template switch).
  const customTitle = useRef<string | null>(
    savedTitle && savedTitle !== state.template?.name ? savedTitle : null,
  );
  const lastSaved = useRef<string | null>(null);
  const pending = useRef<DictatedReportSave | null>(null);
  const running = useRef(false);
  const generation = useRef(0);

  const flush = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    const mine = generation.current;
    try {
      while (pending.current && mine === generation.current) {
        const body = pending.current;
        pending.current = null;
        setStatus("saving");
        try {
          const saved = await saveDictatedReport(body, idRef.current);
          if (mine !== generation.current) return;
          idRef.current = saved;
          setId(saved);
          lastSaved.current = JSON.stringify(body);
          if (!pending.current) setStatus("saved");
        } catch {
          if (mine !== generation.current) return;
          // Kept for Retry, unless a newer save is already waiting.
          pending.current ??= body;
          setStatus("error");
          return;
        }
      }
    } finally {
      running.current = false;
    }
  }, []);

  // A reopened report: what is on screen is what is saved.
  const seeded = useRef(false);
  if (!seeded.current && savedId) {
    seeded.current = true;
    const body = dictatedReportBody(state, "");
    lastSaved.current = body ? JSON.stringify({ ...body, title: "" }) : null;
  }

  useEffect(() => {
    if (phase !== "idle") return;
    const template = state.template;
    if (!template) return;
    const body = dictatedReportBody(
      state,
      dictatedReportTitle(template.name, customTitle.current),
    );
    if (!body) return;
    const key = JSON.stringify(body);
    const unchangedReopen =
      lastSaved.current === JSON.stringify({ ...body, title: "" });
    if (key === lastSaved.current || unchangedReopen) return;
    pending.current = body;
    void flush();
  }, [state, phase, flush]);

  const retry = useCallback(() => {
    void flush();
  }, [flush]);

  // Start a new report: the next save creates another visit.
  const forget = useCallback(() => {
    generation.current += 1;
    pending.current = null;
    running.current = false;
    idRef.current = null;
    customTitle.current = null;
    lastSaved.current = null;
    setId(null);
    setStatus("idle");
  }, []);

  return { id, status, retry, forget };
}
